import { apiClient } from '@/api/client'
import {
  EXPENSE_APPROVALS,
  MY_EXPENSE_CLAIMS,
  MY_EXPENSE_CLAIM_OPTIONS,
  expenseClaimPath,
  expenseClaimReceiptPath,
  myExpenseClaimReceiptPath,
  myExpenseClaimWithdrawPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  ApprovalsQuery,
  ClaimListResponse,
  ClaimOptions,
  ExpenseClaimView,
  MyClaimsQuery,
  SubmitClaimDto,
} from '@/features/expense-claims/types/expenseClaim.types'

const EMPTY_LIST: ClaimListResponse = { data: [], pagination: { total: 0, page: 1, limit: 10 } }

function unwrapClaim(data: ApiResponseWrapper<ExpenseClaimView> | undefined): ExpenseClaimView {
  const claim = data?.data
  if (!claim?.id) throw new Error('Invalid expense claim response')
  return claim
}

function unwrapList(data: ApiResponseWrapper<ClaimListResponse> | undefined): ClaimListResponse {
  const payload = data?.data
  return payload && Array.isArray(payload.data) ? payload : EMPTY_LIST
}

// ─── Self-service ──────────────────────────────────

export async function getMyClaimOptions(): Promise<ClaimOptions> {
  const { data } = await apiClient.get<ApiResponseWrapper<ClaimOptions>>(MY_EXPENSE_CLAIM_OPTIONS)
  return {
    categories: data?.data?.categories ?? [],
    companies: data?.data?.companies ?? [],
  }
}

export async function listMyClaims(query: MyClaimsQuery): Promise<ClaimListResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<ClaimListResponse>>(MY_EXPENSE_CLAIMS, {
    params: query,
  })
  return unwrapList(data)
}

export async function submitClaim(body: SubmitClaimDto): Promise<ExpenseClaimView> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseClaimView>>(
    MY_EXPENSE_CLAIMS,
    body
  )
  return unwrapClaim(data)
}

export async function withdrawClaim(id: string): Promise<ExpenseClaimView> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseClaimView>>(
    myExpenseClaimWithdrawPath(id)
  )
  return unwrapClaim(data)
}

export async function downloadMyClaimReceipt(id: string, uploadId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(myExpenseClaimReceiptPath(id, uploadId), {
    responseType: 'blob',
  })
  return data
}

// ─── Approvals ─────────────────────────────────────

export async function listApprovals(query: ApprovalsQuery): Promise<ClaimListResponse> {
  const { data } = await apiClient.get<ApiResponseWrapper<ClaimListResponse>>(EXPENSE_APPROVALS, {
    params: query,
  })
  return unwrapList(data)
}

export async function getClaim(id: string): Promise<ExpenseClaimView> {
  const { data } = await apiClient.get<ApiResponseWrapper<ExpenseClaimView>>(expenseClaimPath(id))
  return unwrapClaim(data)
}

export async function downloadClaimReceipt(id: string, uploadId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(expenseClaimReceiptPath(id, uploadId), {
    responseType: 'blob',
  })
  return data
}

export async function approveClaim(id: string, note?: string): Promise<ExpenseClaimView> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseClaimView>>(
    `${expenseClaimPath(id)}/approve`,
    note ? { note } : {}
  )
  return unwrapClaim(data)
}

export async function rejectClaim(id: string, reason: string): Promise<ExpenseClaimView> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseClaimView>>(
    `${expenseClaimPath(id)}/reject`,
    { reason }
  )
  return unwrapClaim(data)
}

export async function markClaimReimbursed(id: string, note: string): Promise<ExpenseClaimView> {
  const { data } = await apiClient.post<ApiResponseWrapper<ExpenseClaimView>>(
    `${expenseClaimPath(id)}/mark-reimbursed`,
    { note }
  )
  return unwrapClaim(data)
}
